import { connect } from 'react-redux';
import { sendGALinkClickEvent } from '@noths/polaris-client-google-analytics';
import { updateFavourite } from '@noths/polaris-client-user-favourites';
import getConfig from 'next/config';

import { ProductList } from 'src/components/organisms/ProductList/components/ProductList/ProductList';
import { productListClick } from 'src/components/organisms/ProductList/modules/actions';
import { selectReferredBy } from 'src/components/organisms/ProductList/modules/selectors';
import type {
  ProductListContainerDispatchProps,
  ProductListContainerOwnProps,
  ProductListContainerStateProps,
} from 'src/components/organisms/ProductList/types';
import { ProductCardImage } from 'src/featureFlags/featureFlags';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectPageType } from 'src/redux/navigation/selectors';
import { selectProviderAttributionToken } from 'src/redux/products/selectors/selectors';
import type { UpdateHistoryPayload } from 'src/redux/products/thunks/updatePageHistory';
import { updatePageHistory } from 'src/redux/products/thunks/updatePageHistory';
import type { AppThunkDispatch } from 'src/redux/store';
import { actions as visibilityTrackingActions } from 'src/redux/visibilityTracking/slice';
import { trackProductMediaInteraction } from 'src/tracking/events/productMediaInteraction';
import type { NextConfig } from 'src/types/nextConfig';
import { isPartnerPage } from 'src/utils/page';

const nextConfig = getConfig() as NextConfig;

export const mapStateToProps = (
  state: ReduxApplicationState,
  ownProps: ProductListContainerOwnProps,
): ProductListContainerStateProps => {
  return {
    currency: state.user.currencyCode,
    inListingsCallout: (ownProps.page === 1 && state.content.inListingsCallout) || undefined,
    /* Read from the page type rather than from `content.partner`. State hydration
       merges rather than replaces (`R.mergeDeepRight` in `withStateHydration`) onto a
       singleton client store, so `content.partner` is never cleared and would survive
       a partner -> category soft navigation, suppressing the badge on a grid that is
       not partner scoped. Every page sets its own `pageType`, so that one is always
       overwritten. */
    isSinglePartnerListing: isPartnerPage(selectPageType(state)),
    referredBy: selectReferredBy(state),
    providerAttributionToken: selectProviderAttributionToken(state),
    /* Read against `SINGLE` rather than for `MULTIPLE` on purpose, so that a request
       arriving with no assignment at all renders what the page renders today rather
       than quietly dropping into the control arm.

       Optimizely's own default is `single`, and it governs a bucketed customer: once
       the experiment is live the control arm is sent `single` and loses the gallery
       as intended. This governs the case where no flag reaches the app, which is the
       experiment still being inactive, the worker being bypassed, or the Playwright
       suites turning every flag off. A missing header should not ship a behaviour
       change. */
    showMultipleImages:
      state.user.featureFlags?.[ProductCardImage.NAME] !== ProductCardImage.SINGLE,
  };
};

export const mapDispatchToProps = (
  dispatch: AppThunkDispatch,
): ProductListContainerDispatchProps => {
  return {
    updateFavourite: ({ productCode }: { productCode: number }) =>
      dispatch(updateFavourite({ productCode, nextConfig })),
    updatePageHistory: (payload: UpdateHistoryPayload) => dispatch(updatePageHistory(payload)),
    onInListingsCalloutClick: (ev: React.MouseEvent<HTMLAnchorElement>) => {
      const url = new URL(ev.currentTarget.href);

      sendGALinkClickEvent(ev, {
        event: 'custom_event',
        event_action: 'CTA click',
        event_name: 'select_content',
        event_details: 'cta click',
        content_type: `in-listings callout|${url.pathname}`,
      });
    },
    /* Sent straight from here rather than dispatched through the events map, unlike
       the click below: the event describes only what the customer did to the card, so
       there is no page context or store product to assemble it from. */
    onMediaNavigate: trackProductMediaInteraction,
    onProductVisible: ({ productCode }: { productCode: number }) =>
      dispatch(visibilityTrackingActions.onProductVisible({ productCode })),
    onProductClick: ({
      destinationUrl,
      linkOpensInSameWindow,
      productCode,
      productUrl,
    }: {
      destinationUrl: string;
      linkOpensInSameWindow: boolean;
      productCode: number;
      productUrl: string;
    }) => {
      dispatch(visibilityTrackingActions.sendProductImpressionBatch());
      dispatch(
        productListClick({ productUrl, destinationUrl, productCode, linkOpensInSameWindow }),
      );
    },
  };
};

export const ProductListContainer = connect(mapStateToProps, mapDispatchToProps)(ProductList);
